import { ApproveType, CountriesClient, CountryContactUpdateDTO, CountryCreateDTO, CountryDetailDTO, CountryUpdateDTO, ICountryContactUpdateDTO, ICountryCreateDTO, ICountryDetailDTO, ICountryUpdateBaseDTO, ICountryUpdateDTO } from 'api/swagger/ApiClient';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { log } from 'framework/script_utilities/dev-decorators';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { CountryDetailData } from 'model/forms/country-detail-data';
import { EmailAddressWrapper, SystemUserContactWrapper, SystemUserWrapper } from 'model/support-types';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { requireServices } from 'support/activation';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { BusinessTypesMapping } from 'api/client/mapping/business-types-mapping';
import { EventHelper } from 'framework/aurelia_tools/event-helper';
import { EnumsService } from 'services/enums-service';

@autoinject
export class AdminCountryDetailScreen {
  id: number = null;
  route: RouteConfig = null;
  model: CountryDetailData = null;
  backup: CountryDetailData = null;
  loaded = false;
  changed = false;
  genericEmailAddressesEditing = false;
  approversEditing = false;
  contactsEditing = false;

  constructor(private keywords: KeywordsService, private enums: EnumsService, private lookups: LookupsService, private api: CountriesClient, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer, private businessTypesMapping: BusinessTypesMapping, private eventHelper: EventHelper) {
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(validationRenderer);
  }

  async canActivate() {
    await requireServices(this.keywords, this.lookups, this.enums);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.id = parseInt(params?.id) || null;
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindDetail(this.id ? await this.api.getById(this.id) : null);
    this.loaded = true;
  }

  async canDeactivate() {
    if (this.editing && this.changed && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @log('debug')
  private bindDetail(dto?: ICountryDetailDTO) {
    this.id = dto?.id ?? null;
    if (dto == null)
      dto = new CountryDetailDTO({
        approvalLevel: null,
        approvalRequirement: null,
        approvers: [],
        approveType: this.enums.approvalTypes.find(x => x.value === ApproveType.Any),
        genericEmailAddresses: [],
        hasSpecificCondition: false,
        icon: null,
        name: null,
        specificCondition: null,
        questions: [],
        isActive: true,
        contacts: [],
        updateISOSData: false
      } as ICountryDetailDTO);
    this.model = {
      ...dto,
      genericEmailAddressesWrapper: dto.genericEmailAddresses.map(x => ({ emailAddress: x } as EmailAddressWrapper)),
      approversWrapper: dto.approvers.map(x => ({ user: this.businessTypesMapping.mapUserToUserContact(x), userId: null } as SystemUserWrapper)),
      contactsWrapper: dto.contacts.map(x => ({ user: this.businessTypesMapping.mapUserToUserContact(x), userId: null, countriesContactEmail: x.countriesContactEmail } as SystemUserContactWrapper)),
    } as CountryDetailData;
    this.backup = cloneDeep(this.model);
  }

  archiveButtonVisible = false;
  restoreButtonVisible = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  @watch((x: AdminCountryDetailScreen) => x.model)
  @watch((x: AdminCountryDetailScreen) => x.editing)
  @watch((x: AdminCountryDetailScreen) => x.changed)
  private updateButtons() {
    this.archiveButtonVisible = this.model.isActive && this.id != null;
    this.restoreButtonVisible = !this.model.isActive && this.id != null;
    this.submitButtonVisible = this.editing && this.changed;
    this.cancelButtonVisible = this.editing && this.changed;
  }

  @computedFrom('id', 'model')
  get editing() {
    return this.id == null || this.model?.isActive;
  }

  async submit() {
    if ((!this.genericEmailAddressesEditing && !this.approversEditing && !this.contactsEditing) || await this.dialogHelper.confirm({ dialogTitle: 'Form contains unconfirmed data.', message: 'You haven\'t confirmed newly added Generic Email Address, Approver or Country Contact. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      await this.validationHelper.validate(this.validationController) && await this.save();
    }
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.changed && this.bindDetail(this.backup);
    }
  }

  async archive() {
    this.bindDetail(await this.api.archiveCountry(this.id));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Country has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
  }

  async restore() {
    this.bindDetail(await this.api.unarchiveCountry(this.id));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Country has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
  }

  private async save() {
    const data = this.getSaveData(this.model);
    let dto = null as ICountryDetailDTO;
    if (this.id)
      dto = await this.api.updateCountry(this.id, new CountryUpdateDTO(data as ICountryUpdateDTO));
    else
      dto = await this.api.createCountry(new CountryCreateDTO(data as ICountryCreateDTO))
    this.bindDetail(dto);
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Country has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: CountryDetailData): ICountryUpdateBaseDTO {
    return {
      name: model.name,
      isoCode2: model.isoCode2,
      isoCode3: model.isoCode3,
      updateISOSData: model.updateISOSData,
      icon: model.icon,
      approvalLevelId: model.approvalLevel.id,
      approvalRequirement: model.approvalRequirement.value,
      approversUniqueIds: model.approversWrapper.map(x => x.user.uniqueid),
      approveType: model.approveType.value,
      genericEmailAddresses: model.genericEmailAddressesWrapper.map(x => x.emailAddress),
      hasSpecificCondition: model.hasSpecificCondition,
      specificCondition: model.specificCondition,
      travelOverview: model.travelOverview,
      contacts: model.contactsWrapper.map(x => new CountryContactUpdateDTO({ countriesContactEmail: x.countriesContactEmail, uniqueId: x.user.uniqueid } as ICountryContactUpdateDTO))
    }
  }

  @computedFrom('id', 'model', 'model.name')
  get title() {
    return this.id == null && this.model?.name == null ? 'New Country' : (this.model != null ? this.model.name : '');
  }

  @computedFrom('id', 'model', 'model.name', 'model.icon')
  get titleWithIcon() {
    return `<i class="${(this.model != null && this.model.icon != null ? this.model.icon : '')}" style="margin-right: 5px;"></i><span>${this.title}</span>`;
  }

  @watch((x: AdminCountryDetailScreen) => x.model)
  private modelChanged() {
    this.route.navModel.setTitle(this.title);
    this.validationController.reset();
  }
}
